/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { Panel } from "../../../../ui/components/Panel";
import { useContextMenu, useStateWithEvent } from "../../../../ui/hooks";
import { ReadmeModel } from "../../Common/ReadmeModel";
import { ITreeViewProps, TreeView } from "../../../../ui/components/Tree/TreeView";
import { useTreeState } from "../../../../ui/components/Tree/useTreeState";
import { ITreeNode } from "../../../../ui/components/Tree/TreeViewTypes";
import { isSchemaConstArrayModel, isSchemaConstObjectModel, TGetBlueprintSchemaModel } from "@adapptio/lib-blueprint";
import { TBlueprintReadmeSchema } from "@adapptio/lib-core";

function getPagesTree(
	model: TGetBlueprintSchemaModel<TBlueprintReadmeSchema>
): ITreeNode[] {
	const pagesModel = model.props.spec.props.pages;
	const items: ITreeNode[] = [];

	for (let i = 0; i < pagesModel.items.length; i++) {
		const pageModel = pagesModel.items[i];

		const treeNode: ITreeNode = {
			id: String(pageModel.nodeId),
			label: pageModel.props.title.value || "Untitled",
			icon: "mdi/file-document-outline",
			selectable: true,
			metaData: {
				modelNode: pageModel,
				primaryListModel: pagesModel
			}
		};

		items.push(treeNode);
	}

	return items;
}

export interface IReadmeEditorOutlineProps {
	className?: string;
	readmeModel: ReadmeModel;
}

export const ReadmeEditorOutline: React.FC<IReadmeEditorOutlineProps> = ({ className, readmeModel }) => {
	const baseClass = BEM("edt-readme-editor-outline");

	const selection = useMemo(() => readmeModel.getSelection(), [ readmeModel ]);

	const { showMenu } = useContextMenu("outlineNode");

	const [ treeState, setTreeState ] = useTreeState({ nodes: {} });

	const selectedNodes = useStateWithEvent(selection.onChange, () =>
		selection.getItems().map((item) => String(item.nodeId))
	);

	const handleContextMenuClick = useCallback<ITreeViewProps["onNodeContextMenu"]>(
		(treeNode, event) => {
			showMenu(event, { treeNode });
		},
		[ showMenu ]
	);

	const handleSelect = useCallback(
		(node: ITreeNode) => {
			const modelNode = node.metaData?.modelNode;
			const primaryListModel = node.metaData?.primaryListModel;

			if (isSchemaConstArrayModel(primaryListModel) && isSchemaConstObjectModel(modelNode)) {
				selection.selectPage(primaryListModel, modelNode);
				return;
			}
		},
		[ selection ]
	);

	const treeItems = useStateWithEvent(
		readmeModel.onModelChange,
		() => getPagesTree(readmeModel.getModel()),
		[]
	);

	const handleNodeDragDrop = useCallback<ITreeViewProps["onNodeDragDrop"]>(
		({ draggedNode, dropTargetNode }) => {
			const itemModel = draggedNode.metaData?.modelNode;
			const targetModel = dropTargetNode.metaData?.modelNode;

			const listModel = draggedNode.metaData?.primaryListModel;

			if (isSchemaConstArrayModel(listModel) && isSchemaConstObjectModel(itemModel)) {
				const currentIndex = listModel.items.findIndex(
					(item) => item === itemModel
				);

				const targetNodeIndex = targetModel && isSchemaConstObjectModel(targetModel)
					? listModel.items.findIndex((item) => item === targetModel)
					: null;

				// Compensate for removal if moving upwards
				const newIndex = targetNodeIndex !== null
					? (targetNodeIndex > currentIndex ? targetNodeIndex : targetNodeIndex + 1)
					: 0;

				listModel.schema.moveElementByModel(
					listModel,
					itemModel,
					newIndex,
					true
				);

				return;
			}
		},
		[]
	);

	const isNodeDraggable = useCallback((node: ITreeNode) => {
		return isSchemaConstArrayModel(node?.metaData?.primaryListModel) && isSchemaConstObjectModel(node.metaData?.modelNode);
	}, []);

	const isNodeDropAllowed = useCallback<ITreeViewProps["allowDrop"]>(({ draggedNode, dropTargetNode }) => {
		return (
			isSchemaConstArrayModel(dropTargetNode?.metaData?.primaryListModel) &&
			isSchemaConstArrayModel(draggedNode?.metaData?.primaryListModel) &&
			dropTargetNode.metaData.primaryListModel.nodeId === draggedNode.metaData.primaryListModel.nodeId
		);
	}, []);

	if (className) {
		baseClass.add(className);
	}

	return (
		<Panel scroll className={baseClass.toClassName()}>
			<TreeView
				onStateUpdate={setTreeState}
				state={treeState}
				items={treeItems}
				onNodeSelect={handleSelect}
				selections={selectedNodes}
				onNodeContextMenu={handleContextMenuClick}
				onNodeDragDrop={handleNodeDragDrop}
				isDraggable={isNodeDraggable}
				allowDrop={isNodeDropAllowed}
			/>
		</Panel>
	);
};
